import type { LaneId } from "@fate-kingdom/engine";

import { AlphaGameIcon } from "../icons/AlphaGameIcon";

const LANE_X: Readonly<Record<LaneId, number>> = {
  left: 17,
  center: 50,
  right: 83,
};

export interface AlphaAttackPreview {
  readonly sourceLaneId: LaneId;
  readonly targetLaneId: LaneId;
  readonly kind: "Direct" | "Flank";
  readonly attack: number;
  readonly guard: number;
  readonly damage: number;
}

export function AttackPathOverlay({
  sourceLaneId,
  targetLaneIds,
  preview,
}: {
  readonly sourceLaneId?: LaneId | undefined;
  readonly targetLaneIds: readonly LaneId[];
  readonly preview?: AlphaAttackPreview | undefined;
}) {
  if (sourceLaneId === undefined || targetLaneIds.length === 0) return null;
  const sourceX = LANE_X[sourceLaneId];
  return (
    <div
      className="alpha-attack-overlay"
      aria-live="polite"
      data-attack-kind={
        targetLaneIds.every((targetLaneId) => targetLaneId === sourceLaneId) ? "direct" : "flank"
      }
    >
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <marker id="alpha-attack-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3">
            <path d="M0,0 L6,3 L0,6 Z" />
          </marker>
        </defs>
        {targetLaneIds.map((targetLaneId) => {
          const targetX = LANE_X[targetLaneId];
          const direct = targetLaneId === sourceLaneId;
          return (
            <path
              className={direct ? "is-direct" : "is-flank"}
              d={
                direct
                  ? `M ${String(sourceX)} 83 L ${String(targetX)} 18`
                  : `M ${String(sourceX)} 83 Q ${String((sourceX + targetX) / 2 + (targetX > sourceX ? 10 : -10))} 48 ${String(targetX)} 18`
              }
              key={targetLaneId}
              markerEnd="url(#alpha-attack-arrow)"
            />
          );
        })}
      </svg>
      <div className="alpha-attack-preview">
        <span>
          <AlphaGameIcon name="attack" aria-hidden="true" />
          {preview?.kind ?? (targetLaneIds.length === 1 ? "Direct" : "Flank")} Attack
        </span>
        {preview === undefined ? (
          <small>Choose a glowing enemy Land</small>
        ) : (
          <b>
            {preview.attack} ATK <i>→</i> {preview.guard} GRD <i>→</i> {preview.damage} DMG
          </b>
        )}
      </div>
    </div>
  );
}
